import g from '#core/global';
import load from '#core/load';

// Selectors
const map_$ = '.region-map';
const map_container_$ = '.region-map__map';
const svg_$ = '.region-map__svg';
const region_$ = '.region-map__region';
const popover_$ = '.region-map__popover';
const popover_kicker_$ = '.region-map__popover-kicker';
const popover_title_$ = '.region-map__popover-title';
const popover_abstract_$ = '.region-map__popover-abstract';
const popover_link_$ = '.region-map__popover-link';
const popover_link_text_$ = '.region-map__popover-link-text';
const select_$ = '.region-map__select select';
const small_region_$ = '.region-map__small-region';
const data_$ = '.region-map__data';

// Class names
const region_enabled_c = 'region-map__region--enabled';
const region_disabled_c = 'region-map__region--disabled';
const region_highlight_c = 'region-map__region--highlight';
const region_active_c = 'region-map__region--active';
const popover_visible_c = 'region-map__popover--visible';
const small_region_disabled_c = 'region-map__small-region--disabled';

// Attributes
const region_id_attr = 'data-region-id';

// Timing
const HIDE_DELAY = 150;

// Popover offset from cursor
const CURSOR_OFFSET = 12;

// Minimum distance from viewport edge
const VIEWPORT_BUFFER = 20;

// Module state
let $maps = null;
let has_map = false;

const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('region-map')) return false;

		load.content(() => {
			has_map = assign_elements();
			if (!has_map) return false;
		});
	},

};

export default module;
module.init();


/**
 * Assign elements and initialize each map instance
 */
function assign_elements() {
	$maps = document.querySelectorAll(map_$);
	if (!$maps.length) return false;

	$maps.forEach($map => {
		init_map($map);
	});

	return true;
}


/**
 * Initialize a single map instance
 * @param {HTMLElement} $map
 * @return {boolean}
 */
function init_map($map) {
	const $map_container = $map.querySelector(map_container_$);
	const $svg = $map.querySelector(svg_$);
	const $popover = $map.querySelector(popover_$);
	const $select = $map.querySelector(select_$);
	const $data_el = $map.querySelector(data_$);

	if (!$svg || !$data_el) return false;

	// Instance state
	let regions_data = {};
	let mode = 'click';
	let current_region_id = null;
	let hide_timeout = null;
	let last_cursor_x = 0;
	let last_cursor_y = 0;

	// Parse region data
	try {
		const parsed_data = JSON.parse($data_el.textContent);
		mode = parsed_data.mode || 'click';
		regions_data = { ...parsed_data };
		delete regions_data.mode;
	} catch (e) {
		console.error('Region Map: Failed to parse region data');
		return false;
	}

	// Apply data attributes to SVG regions
	apply_region_data($svg, regions_data, mode);

	// Static mode: no event binding needed
	if (mode === 'static') return;

	// Interactive modes require popover
	if (!$popover) return false;


	// --- Popover Content Update ---

	function update_popover_content(id) {
		const data = regions_data[id];
		if (!data || !data.enabled || !data.popover) return false;

		const popover = data.popover;
		$popover.querySelector(popover_kicker_$).textContent = popover.kicker || data.name;
		$popover.querySelector(popover_title_$).textContent = popover.title || '';
		$popover.querySelector(popover_abstract_$).textContent = popover.abstract || '';

		const $link = $popover.querySelector(popover_link_$);
		$link.href = popover.link_url || data.url || '#';
		$popover.querySelector(popover_link_text_$).textContent = popover.link_text || 'Learn More';

		return true;
	}


	// --- Popover Show/Hide Logic ---

	function show_popover(id, cursor_x, cursor_y) {
		clear_hide_timeout();

		const data = regions_data[id];
		if (!data || !data.enabled) return;

		// Unhighlight previous region before switching
		if (current_region_id && current_region_id !== id) {
			highlight_region($svg, current_region_id, false);
			if (mode === 'click') activate_region($svg, current_region_id, false);
		}

		current_region_id = id;
		highlight_region($svg, id, true);

		// Only show popover if region has popover data
		if (!data.popover) return;

		// Update content
		update_popover_content(id);

		// Position popover at cursor
		position_popover_at_cursor($popover, cursor_x, cursor_y, $map_container);

		// Show
		$popover.classList.add(popover_visible_c);
		$popover.setAttribute('aria-hidden', 'false');

		if (mode === 'click') activate_region($svg, id, true);
	}


	function update_popover_position(cursor_x, cursor_y) {
		if (!current_region_id) return;
		position_popover_at_cursor($popover, cursor_x, cursor_y, $map_container);
	}


	function hide_popover(immediate = false) {
		if (immediate) {
			do_hide();
		} else {
			hide_timeout = setTimeout(do_hide, HIDE_DELAY);
		}
	}


	function do_hide() {
		$popover.classList.remove(popover_visible_c);
		$popover.setAttribute('aria-hidden', 'true');

		if (current_region_id) {
			highlight_region($svg, current_region_id, false);
			activate_region($svg, current_region_id, false);
			current_region_id = null;
		}
	}


	function clear_hide_timeout() {
		if (hide_timeout) {
			clearTimeout(hide_timeout);
			hide_timeout = null;
		}
	}


	// --- Mode: Hover ---

	if (mode === 'hover') {

		// Mousemove: show/update popover at cursor
		$svg.addEventListener('mousemove', e => {
			const $region = e.target.closest(region_$);
			last_cursor_x = e.clientX;
			last_cursor_y = e.clientY;

			if ($region && $region.classList.contains(region_enabled_c)) {
				const id = $region.getAttribute(region_id_attr);

				if (id !== current_region_id) {
					// New region - show popover
					show_popover(id, e.clientX, e.clientY);
				} else {
					// Same region - update position
					update_popover_position(e.clientX, e.clientY);
				}
			} else {
				// Not over enabled region - hide
				if (current_region_id) {
					hide_popover(true);
				}
			}
		});

		// Mouseleave: hide popover immediately
		$svg.addEventListener(g.mouse_leave_e, () => {
			hide_popover(true);
		});

		// Click: navigate to URL
		$svg.addEventListener(g.click_e, e => {
			const $region = e.target.closest(region_$);
			if (!$region || !$region.classList.contains(region_enabled_c)) return;

			const id = $region.getAttribute(region_id_attr);
			const data = regions_data[id];
			if (data && data.url) {
				window.location.href = data.url;
			}
			$region.blur();
		});

		// Small regions: hover mode
		const $small_regions = $map.querySelectorAll(small_region_$);
		$small_regions.forEach($small => {
			if ($small.classList.contains(small_region_disabled_c)) return;

			$small.addEventListener('mousemove', e => {
				const id = $small.getAttribute(region_id_attr);
				last_cursor_x = e.clientX;
				last_cursor_y = e.clientY;

				if (id !== current_region_id) {
					show_popover(id, e.clientX, e.clientY);
				} else {
					update_popover_position(e.clientX, e.clientY);
				}
			});

			$small.addEventListener(g.mouse_leave_e, () => {
				hide_popover(true);
			});

			$small.addEventListener(g.click_e, () => {
				const id = $small.getAttribute(region_id_attr);
				const data = regions_data[id];
				if (data && data.url) {
					window.location.href = data.url;
				}
				$small.blur();
			});
		});

	}


	// --- Mode: Click ---

	if (mode === 'click') {

		// Mousemove: only track cursor and highlight
		$svg.addEventListener('mousemove', e => {
			const $region = e.target.closest(region_$);
			last_cursor_x = e.clientX;
			last_cursor_y = e.clientY;

			// Get currently hovered region
			const hovered_id = $region && $region.classList.contains(region_enabled_c)
				? $region.getAttribute(region_id_attr)
				: null;

			// Update highlights for all regions
			const $all_regions = $svg.querySelectorAll(region_$);
			$all_regions.forEach($r => {
				const rid = $r.getAttribute(region_id_attr);
				if (rid === hovered_id || rid === current_region_id) {
					$r.classList.add(region_highlight_c);
				} else {
					$r.classList.remove(region_highlight_c);
				}
			});
		});

		// Mouseleave: remove hover highlight but keep active region highlighted
		$svg.addEventListener(g.mouse_leave_e, () => {
			const $all_regions = $svg.querySelectorAll(region_$);
			$all_regions.forEach($r => {
				const rid = $r.getAttribute(region_id_attr);
				if (rid !== current_region_id) {
					$r.classList.remove(region_highlight_c);
				}
			});
		});

		// Click: show/move popover
		$svg.addEventListener(g.click_e, e => {
			const $region = e.target.closest(region_$);
			if (!$region || !$region.classList.contains(region_enabled_c)) {
				// Clicking on map but not on enabled region - close popover
				hide_popover(true);
				return;
			}

			const id = $region.getAttribute(region_id_attr);

			if (id === current_region_id) {
				// Clicking same region - close popover
				hide_popover(true);
			} else {
				// Show popover at cursor position
				show_popover(id, e.clientX, e.clientY);
			}
		});

		// Popover hover: keep open (for clickable links)
		$popover.addEventListener(g.mouse_enter_e, () => {
			clear_hide_timeout();
		});

		$popover.addEventListener(g.mouse_leave_e, () => {
			// Don't auto-hide in click mode, user must click elsewhere
		});

		// Small regions: click mode
		const $small_regions = $map.querySelectorAll(small_region_$);
		$small_regions.forEach($small => {
			if ($small.classList.contains(small_region_disabled_c)) return;

			$small.addEventListener(g.mouse_enter_e, () => {
				const id = $small.getAttribute(region_id_attr);
				// Highlight both small region and SVG region
				highlight_region($svg, id, true);
			});

			$small.addEventListener(g.mouse_leave_e, () => {
				const id = $small.getAttribute(region_id_attr);
				// Only remove highlight if not the current active region
				if (id !== current_region_id) {
					highlight_region($svg, id, false);
				}
			});

			$small.addEventListener(g.click_e, e => {
				const id = $small.getAttribute(region_id_attr);

				if (id === current_region_id) {
					hide_popover(true);
				} else {
					show_popover(id, e.clientX, e.clientY);
				}
			});
		});

		// Click outside: close popover
		document.addEventListener(g.click_e, e => {
			if (!$popover.classList.contains(popover_visible_c)) return;

			const is_inside_map = e.target.closest(map_$) === $map;
			const is_popover = e.target.closest(popover_$);

			if (!is_inside_map && !is_popover) {
				hide_popover(true);
			}
		});

	}


	// --- Keyboard: Escape closes popover (both modes) ---

	document.addEventListener(g.keydown_e, e => {
		if (e.key !== 'Escape') return;
		if (!$popover.classList.contains(popover_visible_c)) return;

		hide_popover(true);
	});


	// --- Keyboard: Enter/Space on region ---

	$svg.addEventListener(g.keydown_e, e => {
		if (e.key !== 'Enter' && e.key !== ' ') return;

		const $region = e.target.closest(region_$);
		if (!$region || !$region.classList.contains(region_enabled_c)) return;

		e.preventDefault();
		const id = $region.getAttribute(region_id_attr);

		if (mode === 'hover') {
			// In hover mode, Enter/Space navigates
			const data = regions_data[id];
			if (data && data.url) {
				window.location.href = data.url;
			}
		} else {
			// In click mode, Enter/Space toggles popover
			if (current_region_id === id) {
				hide_popover(true);
			} else {
				// Position at region center for keyboard
				const rect = $region.getBoundingClientRect();
				show_popover(id, rect.left + rect.width / 2, rect.bottom);
			}
		}
	});


	// --- Mobile Select Navigation ---

	if ($select) {
		$select.addEventListener(g.change_e, () => {
			const url = $select.value;
			if (url) {
				window.location.href = url;
			}
		});
	}
}


/**
 * Apply region data from JSON to SVG elements
 */
function apply_region_data($svg, regions_data, mode) {
	const $regions = $svg.querySelectorAll(region_$);

	$regions.forEach($region => {
		const id = $region.getAttribute(region_id_attr);
		const data = regions_data[id];

		if (!data) return;

		if (data.enabled) {
			$region.classList.add(region_enabled_c);
			$region.classList.remove(region_disabled_c);
			$region.setAttribute('aria-label', data.name);

			// Only add interactive attributes for non-static mode with a URL
			if (mode !== 'static' && data.url) {
				$region.setAttribute('tabindex', '0');
				$region.setAttribute('role', 'button');
				$region.setAttribute('aria-haspopup', 'dialog');
			}
		} else {
			$region.classList.add(region_disabled_c);
			$region.classList.remove(region_enabled_c);
			$region.setAttribute('aria-hidden', 'true');
			$region.removeAttribute('tabindex');
			$region.removeAttribute('role');
		}
	});
}


/**
 * Highlight/unhighlight a region
 */
function highlight_region($svg, id, highlight) {
	const $region = $svg.querySelector(`[${region_id_attr}="${id}"]`);
	if (!$region) return;

	if (highlight) {
		$region.classList.add(region_highlight_c);
	} else {
		$region.classList.remove(region_highlight_c);
	}
}


/**
 * Activate/deactivate a region (selected state in click mode)
 */
function activate_region($svg, id, active) {
	const $region = $svg.querySelector(`[${region_id_attr}="${id}"]`);
	if (!$region) return;

	if (active) {
		$region.classList.add(region_active_c);
	} else {
		$region.classList.remove(region_active_c);
	}
}


/**
 * Position popover at cursor with viewport awareness
 * Anchors popover corner at cursor, adjusting which corner based on available space
 */
function position_popover_at_cursor($popover, cursor_x, cursor_y, $container) {
	const container_rect = $container.getBoundingClientRect();

	// Get popover dimensions (need to make it visible briefly to measure)
	$popover.style.visibility = 'hidden';
	$popover.style.display = 'block';
	const popover_rect = $popover.getBoundingClientRect();
	$popover.style.visibility = '';
	$popover.style.display = '';

	const popover_width = popover_rect.width;
	const popover_height = popover_rect.height;

	// Calculate position relative to container
	let rel_x = cursor_x - container_rect.left + CURSOR_OFFSET;
	let rel_y = cursor_y - container_rect.top + CURSOR_OFFSET;

	// Check viewport overflow (with buffer from edge)
	const viewport_width = window.innerWidth;
	const viewport_height = window.innerHeight;

	const is_overflow_right = cursor_x + CURSOR_OFFSET + popover_width + VIEWPORT_BUFFER > viewport_width;
	const is_overflow_bottom = cursor_y + CURSOR_OFFSET + popover_height + VIEWPORT_BUFFER > viewport_height;

	// Adjust anchor corner based on overflow
	if (is_overflow_right) {
		// Anchor right edge instead of left
		rel_x = cursor_x - container_rect.left - popover_width - CURSOR_OFFSET;
	}

	if (is_overflow_bottom) {
		// Anchor bottom edge instead of top
		rel_y = cursor_y - container_rect.top - popover_height - CURSOR_OFFSET;
	}

	// Ensure popover stays within viewport with buffer
	const min_x = VIEWPORT_BUFFER - container_rect.left;
	const min_y = VIEWPORT_BUFFER - container_rect.top;
	const max_x = viewport_width - VIEWPORT_BUFFER - container_rect.left - popover_width;
	const max_y = viewport_height - VIEWPORT_BUFFER - container_rect.top - popover_height;

	rel_x = Math.max(min_x, Math.min(rel_x, max_x));
	rel_y = Math.max(min_y, Math.min(rel_y, max_y));

	$popover.style.left = `${rel_x}px`;
	$popover.style.top = `${rel_y}px`;
}
